import React, { useState } from "react";
import AddForeignEducationSchoolModal from "./AddForeignEducationSchoolModal";
import { Button, FormColumn, Icon, RadioGroup, SubSection, useLoading } from "best-common-react";
import { useTranslation } from "react-i18next";
import { useDropdowns } from "../../../../contexts/DropdownsContext";
import { useProspectProfile } from "../../../../contexts/ProsepctProfileContext";
import styled from "styled-components";
import { LinkButtonStyles } from "../contact-info/ContactInformationStyles";
import { ForeignEducationSchoolDTO } from "../../../../types/ProspectProfile";
import { deleteForeignEducationSchool, saveForeignEducationSchool } from "../../../../api/AuthenticatedClient";
import { Label, Value } from "../../read-only/ReadOnlyProfileStyles";
import { formatLocalDate } from "../../../../util/ProfileUtils";

const Asterisk = styled.span`
  color: ${(props) => props.theme.colors["mlb-red"]};
`;

const ButtonText = styled.span`
  margin-left: 0.25rem;
`;

const Card = styled.div`
  display: flex;
  padding: 0.5rem;
  border: 1px solid ${(props) => props.theme.colors.fog};
  background: ${(props) => props.theme.colors.white};
  justify-content: space-between;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CubanBornEducationInfo: React.FC = () => {
  const { t } = useTranslation(["profile", "translation"]);
  const { setLoading } = useLoading();
  const { yesNoRadioValues, getYesNoRadioValues } = useDropdowns();
  const { profile, updateProfileField } = useProspectProfile();
  const [showForeignEducationSchoolModal, setShowForeignEducationSchoolModal] = useState(false);

  const onDelete = async (id: number) => {
    try {
      setLoading(true);
      await deleteForeignEducationSchool(id);
      updateProfileField(
        "foreignSchools",
        profile.foreignSchools.filter((school) => school.foreignEducationSchoolId !== id),
      );
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (toSave: ForeignEducationSchoolDTO) => {
    try {
      setLoading(true);
      const saved = await saveForeignEducationSchool(toSave);
      updateProfileField("foreignSchools", [...profile.foreignSchools, saved]);
      setShowForeignEducationSchoolModal(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AddForeignEducationSchoolModal
        show={showForeignEducationSchoolModal}
        onSave={onSave}
        onClose={() => setShowForeignEducationSchoolModal(false)}
      />
      <div className="row mt-3">
        <FormColumn width={1}>
          <RadioGroup
            id="has-foreign-education-radio-buttons"
            label={t("profile:hasForeignEducationQuestion")}
            name="hasForeignEducation"
            className="pb-2"
            required
            options={yesNoRadioValues.sort((a, b) => (a.value > b.value ? -1 : 1))}
            value={getYesNoRadioValues(profile?.hasForeignEducation)?.value}
            onChange={(value) => {
              updateProfileField("hasForeignEducation", value === 1);
            }}
          />
        </FormColumn>
      </div>

      {profile?.hasForeignEducation === true && (
        <>
          <div className="row pb-3">
            <SubSection
              header={
                <span>
                  {t("profile:schools")} <Asterisk>*</Asterisk>
                </span>
              }
            >
              {profile?.foreignSchools.map((school) => (
                <Card key={`foreign-school-${school.foreignEducationSchoolId}`}>
                  <Column style={{ width: "90%" }}>
                    <div>
                      <Label>{t("profile:foreignSchoolName")}</Label>
                      <Value>{school.schoolName}</Value>
                    </div>
                    <div>
                      <Label>{t("profile:dateOfLastClass")}</Label>
                      <Value>{formatLocalDate(school.lastClassDate)}</Value>
                    </div>
                  </Column>
                  <Column style={{ width: "10%", alignItems: "end" }}>
                    <Icon iconName="fa-trash-alt" onClick={() => onDelete(school.foreignEducationSchoolId)} />
                  </Column>
                </Card>
              ))}
            </SubSection>
          </div>
          <div className="pb-3">
            <Button
              variant="link"
              className="ps-0"
              styles={LinkButtonStyles}
              onClick={() => setShowForeignEducationSchoolModal(true)}
            >
              <i className="fa-solid fa-circle-plus" />
              <ButtonText>{t("profile:addForeignSchool")}</ButtonText>
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default CubanBornEducationInfo;
